/* ==========================================================================
   NETWORK & API DISPATCHER (/database/api.js)
   ========================================================================== */

import { state, getExactSelectedComponentSpecs, getSelectedComponentIdentity, getCaseModeSelection, getCaseStateKey } from './state.js?v=20260819-audit2';
import { formatCaseBrandTitle, AIO_COMBOBOX_IDS, getComboboxInputValue } from './utils.js?v=20260819-audit2';
import { checkCompatibility } from './compatibility-engine.js?v=20260819-audit2';
import { debugRuntimeIssue } from './runtime.js?v=20260819-audit2';
import { getFilteredResult } from './js/runtime-index.js?v=20260819-audit2';

const AUDIT_REQUEST_SCHEMA_VERSION = 2;
const AUDIT_AUTHORITY_POLICY_VERSION = 2;

const caseCommunityBuildRequestCache = new Map();
const CASE_COMMUNITY_BUILD_CACHE_LIMIT = 128;
const caseReviewRequestCache = new Map();
const CASE_REVIEW_CACHE_LIMIT = 128;

function rememberCommunityBuildRequest(key, value) {
  caseCommunityBuildRequestCache.set(key, value);
  while (caseCommunityBuildRequestCache.size > CASE_COMMUNITY_BUILD_CACHE_LIMIT) {
    caseCommunityBuildRequestCache.delete(caseCommunityBuildRequestCache.keys().next().value);
  }
  return value;
}

function rememberReviewRequest(key, value) {
  caseReviewRequestCache.set(key, value);
  while (caseReviewRequestCache.size > CASE_REVIEW_CACHE_LIMIT) {
    caseReviewRequestCache.delete(caseReviewRequestCache.keys().next().value);
  }
  return value;
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function finiteNumberOrZero(value) {
  return finiteNumberOrNull(value) ?? 0;
}

function stringOrNull(value) {
  const text = String(value ?? '').trim();
  return text ? text : null;
}

function positiveDimension(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function nominalMotherboardOutline(formFactor = 'ITX') {
  const ff = String(formFactor || 'ITX').trim().toUpperCase().replace(/[_ ]+/g, '-');
  if (ff === 'ATX') return { length_mm: 305, width_mm: 244 };
  if (ff === 'MATX' || ff === 'M-ATX' || ff === 'MICRO-ATX') return { length_mm: 244, width_mm: 244 };
  if (ff === 'DTX' || ff === 'MINI-DTX') return { length_mm: 203, width_mm: 170 };
  return { length_mm: 170, width_mm: 170 };
}

function nominalPsuEnvelope(formFactor = '') {
  const ff = String(formFactor || '').trim().toUpperCase().replace(/[_ ]+/g, '-');
  if (ff === 'SFX-L' || ff === 'SFXL') return { length_mm: 125, width_mm: 125, height_mm: 63.5 };
  if (ff === 'SFX') return { length_mm: 100, width_mm: 125, height_mm: 63.5 };
  if (ff === 'ATX') return { length_mm: 140, width_mm: 150, height_mm: 86 };
  if (ff === 'FLEX' || ff === 'FLEX-ATX' || ff === 'FLEXATX') return { length_mm: 150, width_mm: 81.5, height_mm: 40.5 };
  return null;
}

function resolveDimensionEnvelope(rawDimensions, nominalDimensions) {
  const keys = Object.keys(nominalDimensions || rawDimensions || {});
  const dimensions_mm = {};
  const dimension_known = {};
  const dimension_sources = {};
  let catalogCount = 0;

  for (const key of keys) {
    const raw = positiveDimension(rawDimensions?.[key]);
    const fallback = positiveDimension(nominalDimensions?.[key]);
    if (raw !== null) {
      dimensions_mm[key] = raw;
      dimension_known[key] = true;
      dimension_sources[key] = 'catalog';
      catalogCount++;
    } else if (fallback !== null) {
      dimensions_mm[key] = fallback;
      dimension_known[key] = false;
      dimension_sources[key] = 'form_factor_nominal';
    } else {
      dimensions_mm[key] = null;
      dimension_known[key] = false;
      dimension_sources[key] = 'unknown';
    }
  }

  return {
    dimensions_mm,
    dimension_known,
    dimension_sources,
    dimension_source: keys.length && catalogCount === keys.length
      ? 'catalog_full'
      : catalogCount > 0
        ? 'catalog_partial_form_factor_fallback'
        : Object.values(dimensions_mm).some(value => value !== null)
          ? 'form_factor_nominal'
          : 'unknown'
  };
}

function resolveMotherboardDimensionEnvelope(specs = {}, formFactor = 'ITX') {
  return resolveDimensionEnvelope(
    { length_mm: specs?.length_mm, width_mm: specs?.width_mm },
    nominalMotherboardOutline(formFactor)
  );
}

function resolvePsuDimensionEnvelope(specs = {}, formFactor = '') {
  return resolveDimensionEnvelope(
    { length_mm: specs?.length_mm, width_mm: specs?.width_mm, height_mm: specs?.height_mm },
    nominalPsuEnvelope(formFactor)
  );
}

function boolVerdictToInt(value) {
  return value === true ? 1 : (value === false ? 0 : null);
}

function serializeKnownMap(known = {}) {
  return {
    max_gpu_length_mm: !!known.maxGPU,
    max_gpu_thickness_mm: !!known.maxGPUT,
    max_gpu_height_mm: !!known.maxGPUH,
    max_cpu_cooler_height_mm: !!known.maxCPU,
    max_aio_radiator_mm: !!known.maxAIO,
    max_psu_length_mm: !!known.maxPSU
  };
}

function serializeEffectiveClearances(specs = {}) {
  return {
    max_gpu_length_mm: finiteNumberOrZero(specs.maxGPU),
    max_gpu_thickness_mm: finiteNumberOrZero(specs.maxGPUT),
    max_gpu_height_mm: finiteNumberOrZero(specs.maxGPUH),
    max_cpu_cooler_height_mm: finiteNumberOrZero(specs.maxCPU),
    max_aio_radiator_mm: finiteNumberOrZero(specs.maxAIO),
    max_psu_length_mm: finiteNumberOrZero(specs.maxPSU),
    known: serializeKnownMap(specs.known || {})
  };
}

function deriveHeadroomSnapshot(caseObj, evalForm, localRes) {
  const specs = localRes?.activeSpecs || {};
  const known = specs.known || {};
  const gpuApplicable = !caseObj?._isAPU;
  const isAir = String(evalForm?.cooling || '').toLowerCase() === 'air';
  const isLiquid = String(evalForm?.cooling || '').toLowerCase() === 'liquid';

  const delta = (isKnown, maxValue, selectedValue, applicable = true) => {
    if (!applicable || !isKnown) return null;
    const max = finiteNumberOrNull(maxValue);
    const selected = finiteNumberOrNull(selectedValue);
    if (max === null || selected === null || max >= 9999) return null;
    return max - selected;
  };

  const headroom = {
    gpu_length_mm: delta(known.maxGPU, specs.maxGPU, evalForm?.gpuL, gpuApplicable),
    gpu_thickness_mm: delta(known.maxGPUT, specs.maxGPUT, evalForm?.gpuT, gpuApplicable),
    gpu_height_mm: delta(known.maxGPUH, specs.maxGPUH, evalForm?.gpuH, gpuApplicable),
    cpu_cooler_height_mm: delta(known.maxCPU, specs.maxCPU, evalForm?.cpuH, isAir),
    aio_radiator_size_mm: delta(known.maxAIO, specs.maxAIO, evalForm?.rad, isLiquid),
    pcie_slots: gpuApplicable && caseObj?._known?.pcie
      ? delta(true, caseObj._pcie, evalForm?.pcie, true)
      : null
  };

  const atLimit = Object.entries(headroom)
    .filter(([, value]) => Number.isFinite(value) && value === 0)
    .map(([field]) => field);

  return { headroom, at_limit: atLimit };
}


function serializeLocalIntelligenceSnapshot(activeEval) {
  if (!activeEval || typeof activeEval !== 'object') return null;

  const ai = activeEval.aiResult && typeof activeEval.aiResult === 'object' ? activeEval.aiResult : null;
  const evidence = activeEval.buildEvidence && typeof activeEval.buildEvidence === 'object' ? activeEval.buildEvidence : null;
  const gnn = activeEval.gnnResult && typeof activeEval.gnnResult === 'object' ? activeEval.gnnResult : null;

  const edgeAi = ai ? {
    role: 'residual_risk_advisory',
    source: ai.source || null,
    model_available: ai.modelAvailable !== false,
    residual_risk_score: finiteNumberOrNull(ai.residualRiskScore),
    residual_risk_level: ai.residualRiskLevel || null,
    assembly_difficulty_1_to_5: finiteNumberOrNull(ai.difficultyRating),
    cable_risk_detected: !!ai.cableRiskDetected,
    inference_skipped: !!ai.inferenceSkipped,
    override_reason: ai.overrideReason || null
  } : null;

  const documentedEvidence = evidence ? {
    role: 'positive_real_world_evidence',
    source: evidence.source || 'published-community-builds',
    band: evidence.band || 'NONE',
    total_case_builds: finiteNumberOrZero(evidence.totalCaseBuilds),
    evidence_eligible_builds: finiteNumberOrZero(evidence.evidenceEligibleBuilds),
    documentary_photo_count: finiteNumberOrZero(evidence.documentaryPhotoCount),
    selected_comparable_parts: finiteNumberOrZero(evidence.selectedComparableParts),
    exact_count: finiteNumberOrZero(evidence.exactCount),
    strong_count: finiteNumberOrZero(evidence.strongCount),
    similar_count: finiteNumberOrZero(evidence.similarCount),
    best_match: evidence.bestMatch ? {
      build_id: evidence.bestMatch.buildId ?? null,
      title: evidence.bestMatch.title || '',
      similarity_score: finiteNumberOrNull(evidence.bestMatch.similarityScore),
      compared_count: finiteNumberOrZero(evidence.bestMatch.comparedCount),
      matched_count: finiteNumberOrZero(evidence.bestMatch.matchedCount),
      matched_labels: Array.isArray(evidence.bestMatch.matchedLabels) ? evidence.bestMatch.matchedLabels : [],
      mismatched_labels: Array.isArray(evidence.bestMatch.mismatchedLabels) ? evidence.bestMatch.mismatchedLabels : [],
      case_mode: evidence.bestMatch.caseMode || '',
      photo_count: finiteNumberOrZero(evidence.bestMatch.photoCount)
    } : null,
    negative_evidence_allowed: false
  } : null;

  const interactions = Array.isArray(gnn?.interactions) ? gnn.interactions.map(Number).filter(Number.isFinite) : [];
  const relational = gnn ? {
    role: 'relational_advisory',
    source: 'edge_relational_gnn',
    available: true,
    max_interaction_score: interactions.length ? Math.max(...interactions) : null,
    interaction_count: interactions.length,
    full_tensor_exported: false
  } : null;

  if (!edgeAi && !documentedEvidence && !relational) return null;
  return {
    authority: 'advisory_only',
    edge_ai: edgeAi,
    documented_build_evidence: documentedEvidence,
    relational_gnn: relational
  };
}

function baseCaseKnownSnapshot(c) {
  const known = c?._known || {};
  const dims = Array.isArray(c?._dims) ? c._dims : [];
  return {
    volume_l: Number.isFinite(Number(c?._volume)) && Number(c._volume) > 0,
    dimensions_mm: [0, 1, 2].map(index => Number.isFinite(Number(dims[index])) && Number(dims[index]) > 0),
    max_clearances: {
      max_gpu_length_mm: !!known.maxGPU,
      max_gpu_thickness_mm: !!known.maxGPUT,
      max_gpu_height_mm: !!known.maxGPUH,
      max_cpu_cooler_height_mm: !!known.maxCPU,
      max_aio_radiator_mm: !!known.maxAIO,
      max_psu_length_mm: c?.maxPSU !== null && c?.maxPSU !== undefined && c?.maxPSU !== '',
      top_fan_clearance_mm: finiteNumberOrNull(c?.top_fan_clearance) !== null
    },
    pcie_slots: !!known.pcie,
    motherboard_support: Array.isArray(c?._moboList) && c._moboList.length > 0,
    psu_support: Array.isArray(c?._psuList) && c._psuList.length > 0
  };
}

/**
 * Transmits a versioned, authority-aware build snapshot to the remote audit service.
 *
 * Compatibility remains owned by the local deterministic engine. The remote service
 * may explain, contextualize and surface qualitative risk, but may not override the
 * local fit verdict or reinterpret a known numeric zero as unknown.
 */
export async function dispatchBuildPayload(dom, targetCaseIdx = null, endpointUrl = 'https://db.reviewer.co/api/sff/audit') {
  const targetCase = targetCaseIdx !== null && state.fetchedCasesGlobal[targetCaseIdx]
    ? state.fetchedCasesGlobal[targetCaseIdx]
    : null;
  const targetIsApuOnly = !!targetCase?._isAPU;

  const gpuQuery = document.querySelector('#gpuCombobox .combobox-input')?.value.trim() || '';
  const cpuQuery = document.querySelector('#cpuCoolerCombobox .combobox-input')?.value.trim() || '';
  const aioQuery = getComboboxInputValue(AIO_COMBOBOX_IDS);
  const moboQuery = document.querySelector('#moboCombobox .combobox-input')?.value.trim() || '';
  const psuQuery = document.querySelector('#psuCombobox .combobox-input')?.value.trim() || '';
  const ramQuery = document.querySelector('#ramCombobox .combobox-input')?.value.trim() || '';
  const caseSearchQuery = dom.search?.value?.trim() || '';

  const coolingMode = dom.cooling ? dom.cooling.value : 'air';
  const radFanType = dom.radFanVal ? dom.radFanVal.value : 'standard';
  const fanThickness = radFanType === 'slim' ? 15 : (radFanType === 'thick' || radFanType === '30mm' ? 30 : 25);
  const exactMobo = getExactSelectedComponentSpecs('mobo');
  const exactPsu = getExactSelectedComponentSpecs('psu');
  const moboFormFactor = dom.moboVal ? dom.moboVal.value.toUpperCase() : 'ITX';
  const psuFormFactor = dom.psuVal ? dom.psuVal.value.toUpperCase() : 'SFX';
  // Mirror the 3D catalog normalization contract without importing the 3D module:
  // real catalog dimensions win; standardized form-factor geometry fills only
  // missing axes and remains explicitly marked as nominal provenance.
  const moboDimensions = resolveMotherboardDimensionEnvelope(exactMobo || {}, moboFormFactor);
  const psuDimensions = resolvePsuDimensionEnvelope(exactPsu || {}, psuFormFactor);
  const exactAio = getExactSelectedComponentSpecs('aio');
  const radThickness = exactAio?.actual_thickness_mm ?? 27.2;
  const pumpBlockHeight = exactAio?.pump_block_height_mm ?? 45;
  const totalLiquidStackThickness = radThickness + fanThickness;

  const buildConfig = {
    motherboard: {
      form_factor: moboFormFactor,
      model_query: moboQuery,
      brand: stringOrNull(exactMobo?.brand),
      model: stringOrNull(exactMobo?.model),
      ...moboDimensions,
      exact_specs: exactMobo,
      selection_identity: getSelectedComponentIdentity('mobo')
    },
    power_supply: {
      form_factor: psuFormFactor,
      model_query: psuQuery,
      brand: stringOrNull(exactPsu?.brand),
      model: stringOrNull(exactPsu?.model),
      wattage: finiteNumberOrNull(exactPsu?.wattage),
      catalog_install_length_mm: finiteNumberOrNull(exactPsu?.length_mm),
      ...psuDimensions,
      exact_specs: exactPsu,
      selection_identity: getSelectedComponentIdentity('psu')
    },
    memory: {
      model_query: ramQuery,
      module_height_mm: dom.ramH ? parseFloat(dom.ramH.value) : 35,
      installed_modules: dom.ramModules ? parseInt(dom.ramModules.value, 10) : 2,
      exact_specs: getExactSelectedComponentSpecs('ram'),
      selection_identity: getSelectedComponentIdentity('ram')
    },
    graphics_card: {
      applicable: !targetIsApuOnly,
      model_query: targetIsApuOnly ? '' : gpuQuery,
      length_mm: targetIsApuOnly ? 0 : (dom.gpuL ? parseFloat(dom.gpuL.value) : 304),
      thickness_mm: targetIsApuOnly ? 0 : (dom.gpuT ? parseFloat(dom.gpuT.value) : 40),
      height_mm: targetIsApuOnly ? 0 : (dom.gpuH ? parseFloat(dom.gpuH.value) : 137),
      pcie_slots: targetIsApuOnly ? 0 : (dom.gpuPcie ? parseFloat(dom.gpuPcie.value) : 2),
      exact_specs: targetIsApuOnly ? null : getExactSelectedComponentSpecs('gpu'),
      selection_identity: targetIsApuOnly ? null : getSelectedComponentIdentity('gpu')
    },
    cooling: {
      mode: coolingMode,
      air_cooling: {
        applicable: coolingMode === 'air',
        cooler_model_query: cpuQuery,
        height_mm: dom.cpuH ? parseFloat(dom.cpuH.value) : 47,
        length_mm: getExactSelectedComponentSpecs('cpu')?.length_mm ?? null,
        width_mm: getExactSelectedComponentSpecs('cpu')?.width_mm ?? null,
        exact_specs: getExactSelectedComponentSpecs('cpu'),
        selection_identity: getSelectedComponentIdentity('cpu')
      },
      liquid_cooling: {
        applicable: coolingMode === 'liquid',
        aio_model_query: aioQuery,
        radiator_size_mm: dom.radSize ? parseInt(dom.radSize.value, 10) : 240,
        actual_frame_length_mm: exactAio?.actual_length_mm ?? null,
        actual_frame_width_mm: exactAio?.actual_width_mm ?? null,
        radiator_thickness_mm: radThickness,
        radiator_thickness_source: exactAio?.actual_thickness_mm != null ? 'catalog' : 'standard_default_assumption',
        pump_block_height_mm: pumpBlockHeight,
        pump_block_height_source: exactAio?.pump_block_height_mm != null ? 'catalog' : 'standard_default_assumption',
        fan_profile: radFanType,
        fan_thickness_mm: fanThickness,
        total_stack_thickness_mm: totalLiquidStackThickness,
        exact_specs: getExactSelectedComponentSpecs('aio'),
        selection_identity: getSelectedComponentIdentity('aio')
      }
    },
    active_ui_state: {
      layout_filter: state.activeLayout || 'all',
      max_volume_filter_l: dom.vol ? parseFloat(dom.vol.value) : null,
      case_search_query: caseSearchQuery,
      sort_order: dom.sortSelector ? dom.sortSelector.value : 'vol-high',
      hide_incompatible: !!state.filterFlags.hideIncompatible,
      hide_legacy: !!state.filterFlags.hideLegacy
    }
  };

  let targetCasePayload = null;
  let localEvaluation = null;
  let localIntelligenceSnapshot = null;

  if (targetCase) {
    const c = targetCase;
    const activeModeId = getCaseModeSelection(c, 'default');
    const evalForm = {
      cooling: coolingMode,
      cpuH: dom.cpuH ? parseFloat(dom.cpuH.value) : 47,
      rad: dom.radSize ? parseInt(dom.radSize.value, 10) : 240,
      gpuL: dom.gpuL ? parseFloat(dom.gpuL.value) : 304,
      gpuT: dom.gpuT ? parseFloat(dom.gpuT.value) : 40,
      gpuH: dom.gpuH ? parseFloat(dom.gpuH.value) : 137,
      pcie: dom.gpuPcie ? parseFloat(dom.gpuPcie.value) : 2,
      mobo: dom.moboVal ? dom.moboVal.value.toUpperCase() : 'ITX',
      psu: dom.psuVal ? dom.psuVal.value.toUpperCase() : 'SFX',
      radFan: radFanType,
      ramH: dom.ramH ? parseFloat(dom.ramH.value) : 35,
      ramModules: dom.ramModules ? parseInt(dom.ramModules.value, 10) : 2
    };

    const localRes = checkCompatibility(c, evalForm, activeModeId);
    const activeEval = getFilteredResult(targetCaseIdx) || state.resultByIdx?.get?.(targetCaseIdx) || null;
    localIntelligenceSnapshot = serializeLocalIntelligenceSnapshot(activeEval);
    const effectiveClearances = serializeEffectiveClearances(localRes.activeSpecs || {});
    const { headroom, at_limit: atLimit } = deriveHeadroomSnapshot(c, evalForm, localRes);
    const baseKnown = baseCaseKnownSnapshot(c);

    localEvaluation = {
      schema_version: 2,
      authority: 'local_sff_engine',
      authority_level: 'authoritative',
      verdict: localRes.fits ? 'FIT' : 'NO_FIT',
      verification_state: localRes.fits
        ? ((localRes.unknowns?.length || localRes.warns?.length) ? 'FIT_REQUIRES_VERIFICATION' : 'FIT_VERIFIED')
        : 'NO_FIT',
      fits: localRes.fits,
      fails: Array.isArray(localRes.fails) ? localRes.fails : [],
      warns: Array.isArray(localRes.warns) ? localRes.warns : [],
      unknowns: Array.isArray(localRes.unknowns) ? localRes.unknowns : [],
      advisories: Array.isArray(localRes.advisories) ? localRes.advisories : [],
      active_mode_id: activeModeId,
      active_spine_step: localRes.spineStep || null,
      active_specs: effectiveClearances,
      headroom,
      at_limit: atLimit
    };

    targetCasePayload = {
      case_index: targetCaseIdx,
      case_key: getCaseStateKey(c),
      normalized_specs: {
        identity: {
          catalog_id: c.catalogId ?? c.catalog_id ?? c.case_id ?? c.caseId ?? c.id ?? null,
          case_key: getCaseStateKey(c),
          slug: c._slug || c.case_slug || c.db_slug || c.slug || '',
          name: formatCaseBrandTitle(c.name) || ''
        },
        status: c.status || '',
        volume_l: finiteNumberOrZero(c._volume),
        layout: c._layout || 'reference',
        active_mode_id: activeModeId,
        is_legacy: !!c._isLegacy,
        is_apu_only: !!c._isAPU,
        discrete_gpu_support: c._known?.maxGPU && Number(c._maxGPU) === 0 ? 'unsupported' : (c._known?.maxGPU ? 'supported' : 'unknown'),
        price_usd: finiteNumberOrZero(c._priceVal),
        price_raw: c.priceUSD || '',
        seller: c.seller || 'DIRECT',
        material: c.material || '',
        footprint_cm2: c.footprint || '',
        dimensions: {
          raw_string: c.caseDimensions || '',
          parsed_l_w_h_mm: Array.isArray(c._dims) ? c._dims : [0, 0, 0]
        },
        max_clearances: {
          max_gpu_length_mm: finiteNumberOrZero(c._maxGPU),
          max_gpu_thickness_mm: finiteNumberOrZero(c._maxThick),
          max_gpu_height_mm: finiteNumberOrZero(c._maxGPUH),
          max_cpu_cooler_height_mm: finiteNumberOrZero(c._maxCPU),
          max_aio_radiator_mm: finiteNumberOrZero(c._maxAIO),
          max_psu_length_mm: finiteNumberOrZero(c.maxPSU),
          top_fan_clearance_mm: finiteNumberOrZero(c.top_fan_clearance)
        },
        known: baseKnown,
        effective_clearances: effectiveClearances,
        supported_hardware_sets: {
          motherboards: c._moboList || [],
          psus: c._psuList || []
        },
        expansion_and_io: {
          pcie_slots: finiteNumberOrZero(c.pcieSlots ?? c._pcie),
          gpu_riser_included: c.gpuRiser || 'N',
          usb_c: c.usbC || 'N',
          usb_a_3_2: c.usbA3 || 'N',
          usb_a_2_0: c.usbA2 || 'N',
          jack_3_5mm: c.jack35 || 'N'
        },
        fan_mounts: {
          fans_60mm: c.fans60 || '0',
          fans_80mm: c.fans80 || '0',
          fans_92mm: c.fans92 || '0',
          fans_120mm: c.fans120 || '0',
          fans_140mm: c.fans140 || '0',
          spatial_locations: Array.isArray(c.spatialHints?.fans) ? c.spatialHints.fans : []
        },
        radiator_support: c.radiatorSupport || null,
        radiator_mounts: Array.isArray(c.spatialHints?.radiators) ? c.spatialHints.radiators : [],
        drive_bays: {
          drives_2_5_inch: c.drives25 || '0',
          drives_3_5_inch: c.drives35 || '0',
          drives_5_25_inch: c.drives525 || '0'
        },
        spine_configuration: {
          has_spine: !!c.hasSpine,
          fixed_steps: Array.isArray(c.fixedSteps) ? c.fixedSteps : []
        },
        evaluated_configuration_context: {
          active_mode_id: activeModeId,
          active_mode_name: Array.isArray(c.modes)
            ? (c.modes.find(mode => String(mode?.mode_id || '').toLowerCase() === String(activeModeId).toLowerCase())?.mode_name || '')
            : '',
          dynamic_rules_evaluated_locally: Array.isArray(c.rules) && c.rules.length > 0,
          raw_modes_exported: false,
          raw_rules_exported: false
        },
        spec_notes: c.specNotes || {},
        case_notes: c.notes || (c.specNotes ? Object.values(c.specNotes).join('\n') : ''),
        urls: { seller_url: c.url || '', image_url: c.image || '' }
      }
    };
  }

  const payload = {
    contract_name: 'sff_audit_request',
    contract_version: AUDIT_REQUEST_SCHEMA_VERSION,
    workflow_type: 'sff_compatibility_audit',
    source_domain: window.location.hostname || 'sff.gg',
    timestamp: new Date().toISOString(),
    authority_contract: {
      version: AUDIT_AUTHORITY_POLICY_VERSION,
      compatibility_authority: 'local_sff_engine',
      remote_audit_role: 'qualitative_advisory',
      ai_override_allowed: false,
      fail_open: false,
      local_fit_precedence: 'local_sff_evaluation.fits is authoritative whenever it is boolean',
      known_zero_semantics: 'A known numeric 0 is an explicit unsupported/no-capability value; consult the paired known flags. This is used by APU cases with no discrete-GPU support.',
      unknown_semantics: 'Do not infer knownness from a numeric fallback alone. A field with known=false is unverified even when its numeric transport value is 0.',
      advisory_semantics: 'warns, unknowns and advisories may require verification but cannot create or erase an authoritative hard failure unless represented in local_sff_evaluation.fails.'
    },
    response_contract: {
      version: 2,
      is_compatible_type: 'boolean|null',
      preserve_local_authority: true,
      preserve_unknown_state: true,
      at_limit_source: 'local_sff_evaluation.at_limit',
      compatibility_override_allowed: false,
      is_compatible_semantics: 'If returned, is_compatible must only echo local_sff_evaluation.fits; remote reasoning must not independently derive or override compatibility.',
      qualitative_output_role: 'Explain local results, summarize evidence, identify verification targets and practical assembly concerns.'
    },
    build_configuration: buildConfig,
    target_case: targetCasePayload,
    local_sff_evaluation: localEvaluation,
    local_intelligence_snapshot: localIntelligenceSnapshot
  };

  // Absolute dispatch boundary: reports are advisory analysis for configurations
  // that have already passed the authoritative local compatibility engine.
  // A deterministic NO_FIT must never consume a remote audit run.
  if (localEvaluation?.fits !== true) {
    const failures = Array.isArray(localEvaluation?.fails) ? localEvaluation.fails : [];
    console.warn('[SFF Report] Payload dispatch blocked by local compatibility authority.', {
      targetCaseIdx,
      verdict: localEvaluation?.verdict || 'NO_FIT',
      failures
    });
    alert(
      failures.length
        ? `Report generation is disabled because this configuration does not fit.\n\n${failures.join('\n')}`
        : 'Report generation is disabled because this configuration does not pass the local compatibility check.'
    );
    return null;
  }

  state.activeAuditRequestPayload = payload;

  try {
    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorPayload = null;
      let errorText = '';
      try {
        errorText = await response.text();
        if (errorText) errorPayload = JSON.parse(errorText);
      } catch (_) {}

      const validation = Array.isArray(errorPayload?.validation_errors)
        ? errorPayload.validation_errors.join(', ')
        : '';
      const detail = [
        errorPayload?.error || errorPayload?.message,
        errorPayload?.code,
        validation || errorPayload?.detail
      ].filter(Boolean).join(' · ');

      console.error('[SFF Audit Gateway Error]', {
        status: response.status,
        statusText: response.statusText,
        payload: errorPayload || errorText || null
      });

      throw new Error(`Server returned HTTP ${response.status}${detail ? `: ${detail}` : (response.statusText ? `: ${response.statusText}` : '')}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[SFF Payload Dispatch Failed]:', error);
    alert(`Failed to transmit build payload: ${error.message}`);
    return null;
  }
}

/**
 * Dispatches deterministic user-validation telemetry. This is analytics only;
 * engine_fits_verdict must never be treated as verified real-world ML ground truth.
 */
export async function sendUserInteractionTelemetry(caseObj, formInputs, fitResult) {
  if (!caseObj || !formInputs || !fitResult) return;

  const knownCaseValue = (knownKey, value) => (
    caseObj._known?.[knownKey] && Number.isFinite(Number(value)) ? Number(value) : null
  );
  const engineVerdict = boolVerdictToInt(fitResult.fits);
  const gpuApplicable = !caseObj._isAPU;

  const payload = {
    telemetry_schema_version: 3,
    event_type: 'ENGINE_EVALUATION_SNAPSHOT',
    interaction_context: 'user_validation_action',
    label_source: 'deterministic_engine',
    training_label_eligible: false,
    timestamp: new Date().toISOString(),
    case_key: getCaseStateKey(caseObj),
    case_name: caseObj.name || '',
    volume: finiteNumberOrNull(caseObj._volume),
    max_gpu_len: knownCaseValue('maxGPU', caseObj._maxGPU),
    max_cpu_height: knownCaseValue('maxCPU', caseObj._maxCPU),
    max_aio_rad: knownCaseValue('maxAIO', caseObj._maxAIO),
    selected_gpu_present: gpuApplicable,
    selected_gpu_len: gpuApplicable ? finiteNumberOrNull(formInputs.gpuL) : null,
    selected_gpu_thick: gpuApplicable ? finiteNumberOrNull(formInputs.gpuT) : null,
    selected_gpu_height: gpuApplicable ? finiteNumberOrNull(formInputs.gpuH) : null,
    selected_cpu_height: finiteNumberOrNull(formInputs.cpuH),
    cooling_mode: formInputs.cooling || 'air',
    radiator_size: finiteNumberOrNull(formInputs.rad),
    engine_fits_verdict: engineVerdict,
    // Deprecated analytics alias retained for backend compatibility only.
    fits_verdict: engineVerdict
  };

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon('/api/telemetry.php', blob);
    } else {
      fetch('/api/telemetry.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(error => debugRuntimeIssue('Telemetry Beacon Fallback', error));
    }
  } catch (e) {
    console.warn('[SFF Telemetry Error]:', e);
  }
}

/**
 * On-demand lazy fetcher for community builds & photo galleries.
 */
export async function fetchCaseCommunityBuilds(caseSlug, page = 1, limit = 10) {
  if (!caseSlug) return null;

  const safePage = Math.max(1, Number(page) || 1);
  const safeLimit = Math.max(1, Math.min(50, Number(limit) || 10));
  const cacheKey = `${String(caseSlug).toLowerCase()}|${safePage}|${safeLimit}`;
  if (caseCommunityBuildRequestCache.has(cacheKey)) {
    return caseCommunityBuildRequestCache.get(cacheKey);
  }

  const request = (async () => {
    try {
      const response = await fetch(`/wp-admin/admin-ajax.php?action=get_case_community_builds&case_slug=${encodeURIComponent(caseSlug)}&page=${safePage}&limit=${safeLimit}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      return json.success ? json.data : null;
    } catch (err) {
      caseCommunityBuildRequestCache.delete(cacheKey);
      console.warn('[SFF API]: Community builds fetch failed:', err);
      return null;
    }
  })();

  return rememberCommunityBuildRequest(cacheKey, request);
}

/**
 * On-demand lazy fetcher for editorial review posts and Cornerstone sections
 */
export async function fetchCaseReviews(caseSlug) {
  if (!caseSlug) return null;

  const cacheKey = String(caseSlug).toLowerCase().trim();
  if (caseReviewRequestCache.has(cacheKey)) {
    return caseReviewRequestCache.get(cacheKey);
  }

  const request = (async () => {
    try {
      const response = await fetch(`/wp-admin/admin-ajax.php?action=get_case_reviews&case_slug=${encodeURIComponent(caseSlug)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      return json.success ? json.data : null;
    } catch (err) {
      caseReviewRequestCache.delete(cacheKey);
      console.warn('[SFF API]: Reviews fetch failed:', err);
      return null;
    }
  })();

  return rememberReviewRequest(cacheKey, request);
}
